import type {
  SortModelItem,
  PaginationModel,
  SearchResultItem,
  SearchSubmitPayload,
  SearchAccordionActions,
} from '@ncrb/shared-components';

import { CONFIG } from 'src/global-config';

import type { SearchRestoreSnapshot } from './search-restore-state';

export const SEARCH_PAGE_TITLE = 'Search';
export const SEARCH_PANEL_TITLE = 'Search';

export const POLICY_SEARCH_TAB_ID: SearchTabId = 'policy';
export const EMPLOYER_SEARCH_TAB_ID: SearchTabId = 'employer';
export const USR_SEARCH_TAB_ID: SearchTabId = 'usr';

export const SEARCH_MESSAGES = {
  emptyCriteria: 'Enter at least one search criterion.',
  genericError: 'Unable to load policy search results.',
  usrGenericError: 'Unable to load USR search results.',
  stateLookupLoadError:
    'State options are currently unavailable. You can still search using non-state criteria.',
  marketLookupLoadError:
    'Market options are currently unavailable. You can still search using other criteria.',
  ratingTypeLookupLoadError:
    'Rating status options are currently unavailable. You can still search using other criteria.',
  usrLookupLoadError:
    'USR lookup options are currently unavailable. You can still search using other criteria.',
} as const;

export const metadata = { title: `${SEARCH_PAGE_TITLE} | ${CONFIG.appName}` };

export type SearchTabId =
  | 'policy'
  | 'employer'
  | 'usr'
  | 'ar-apps'
  | 'submission-data'
  | 'work-queue'
  | 'fines';

export type SearchResultRow = {
  id: string;
  policyNumber?: string;
  effectiveDate?: Date | null;
  employerName?: string;
  fein?: string;
  feinLastFive?: string;
  carrierId?: string;
  interstateId?: string;
  address?: string;
  comboId?: number | null;
  coverageId?: number | null;
  policyId?: number | null;
};

export const SEARCH_TAB_ORDER: SearchTabId[] = [
  'policy',
  'employer',
  'usr',
  'ar-apps',
  'submission-data',
  'work-queue',
  'fines',
];

export const SEARCH_TAB_LABELS: Record<SearchTabId, string> = {
  policy: 'Policy',
  employer: 'Employer',
  usr: 'USR',
  'ar-apps': 'AR apps',
  'submission-data': 'Submission data',
  'work-queue': 'Work queue',
  fines: 'Fines',
};

export const SEARCH_RESULTS_COLUMNS = [
  { field: 'policyNumber', headerName: 'Policy number', isLink: true },
  { field: 'effectiveDate', headerName: 'Effective date', type: 'date' as const },
  { field: 'employerName', headerName: 'Employer name' },
  { field: 'feinLastFive', headerName: 'FEIN' },
  { field: 'carrierId', headerName: 'Carrier ID' },
];

export const EMPLOYER_SEARCH_RESULTS_COLUMNS = [
  { field: 'employerName', headerName: 'Employer Name', isLink: false },
  { field: 'comboId', headerName: 'Combo', isLink: true },
  { field: 'coverageId', headerName: 'Coverage', isLink: true },
  { field: 'interstateId', headerName: 'Interstate ID' },
  { field: 'feinLastFive', headerName: 'FEIN' },
  { field: 'address', headerName: 'Address' },
];

export const USR_SEARCH_RESULTS_COLUMNS = [
  { field: 'carrierId', headerName: 'Carrier ID' },
  { field: 'policyNumber', headerName: 'Policy number' },
  { field: 'effectiveDate', headerName: 'Effective date', type: 'date' as const },
  { field: 'unitLevel', headerName: 'Unit level', isLink: true },
  { field: 'unitStatus', headerName: 'Unit status' },
];

export function isValidSearchTabId(value: string | null): value is SearchTabId {
  return !!value && SEARCH_TAB_ORDER.includes(value as SearchTabId);
}

export function resolveTargetSearchTabId(
  tabParam: string | null,
  hash: string,
  stateTab?: string | null
): SearchTabId {
  if (isValidSearchTabId(tabParam)) {
    return tabParam;
  }

  const stateTabCandidate = stateTab ?? null;
  if (isValidSearchTabId(stateTabCandidate)) {
    return stateTabCandidate;
  }

  const hashValue = hash.replace('#', '').trim();
  if (isValidSearchTabId(hashValue)) {
    return hashValue;
  }

  return 'policy';
}

export const SEARCH_RESTORE_QUERY_PARAM = 'restore';
export const SEARCH_RESTORE_QUERY_PARAM_VALUE = '1';

export const SEARCH_ORIGIN_DESTINATIONS = ['combo', 'coverage', 'policyDetails'] as const;

export type SearchOriginDestination = (typeof SEARCH_ORIGIN_DESTINATIONS)[number];

export function isSearchOriginDestination(value: unknown): value is SearchOriginDestination {
  return typeof value === 'string' && (SEARCH_ORIGIN_DESTINATIONS as readonly string[]).includes(value);
}

export function resolveRestoreSnapshot(
  lastSubmission: SearchSubmitPayload | null,
  latestSearchRows: SearchResultItem[],
  latestSearchRowCount: number,
  latestSearchPaginationModel: PaginationModel,
  latestSearchSortModel: SortModelItem[],
  latestSearchRowGroupingModel: string[]
): SearchRestoreSnapshot | null {
  const lastSubmissionTabId = lastSubmission?.tabId;
  let restoreTabId: SearchRestoreSnapshot['tabId'] | null = null;

  if (lastSubmissionTabId === POLICY_SEARCH_TAB_ID) {
    restoreTabId = 'policy';
  } else if (lastSubmissionTabId === EMPLOYER_SEARCH_TAB_ID) {
    restoreTabId = 'employer';
  } else if (lastSubmissionTabId === USR_SEARCH_TAB_ID) {
    restoreTabId = 'usr';
  }

  if (!restoreTabId) {
    return null;
  }

  return {
    source: 'advanced-search',
    tabId: restoreTabId,
    submittedValues: lastSubmission?.values ?? {},
    rows: latestSearchRows,
    rowCount: latestSearchRowCount,
    rowIdField: restoreTabId === POLICY_SEARCH_TAB_ID ? 'policyId' : 'id',
    paginationModel: latestSearchPaginationModel,
    sortModel: latestSearchSortModel,
    rowGroupingModel: latestSearchRowGroupingModel,
  };
}

export function buildSearchRestoreNavigationState(
  restoreSnapshot: SearchRestoreSnapshot | null,
  searchOrigin: SearchOriginDestination
) {
  if (!restoreSnapshot) {
    return undefined;
  }

  return {
    searchTab: restoreSnapshot.tabId,
    searchOrigin,
    searchRestoreSnapshot: restoreSnapshot,
  };
}

export function getPolicyCriteriaHeaderFromTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  return target.closest('#search-criteria-header-policy');
}

export function reopenPolicyAccordions(
  criteriaHeader: HTMLElement,
  policyGroupingState: Record<string, boolean>,
  searchAccordionActions: SearchAccordionActions | null
): void {
  if (!searchAccordionActions) {
    return;
  }

  const isExpanded = criteriaHeader.getAttribute('aria-expanded') === 'true';

  if (!isExpanded) {
    return;
  }

  Object.entries(policyGroupingState).forEach(([groupingId, hasValues]) => {
    if (hasValues) {
      searchAccordionActions.setAccordionExpanded(POLICY_SEARCH_TAB_ID, groupingId, true);
    }
  });
}

export function isAbortLikeError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  return error.name === 'AbortError' || error.name === 'CanceledError';
}

export function extractSearchErrorMessage(error: unknown, fallbackMessage: string): string {
  const apiErrorMessage = extractApiErrorMessage(error);
  if (apiErrorMessage) {
    return apiErrorMessage;
  }

  if (error instanceof Error && !isGenericHttpStatusMessage(error.message)) {
    return error.message;
  }

  return fallbackMessage;
}

function extractApiErrorMessage(error: unknown): string | null {
  const data = extractErrorResponseData(error);
  if (!data) {
    return null;
  }

  const errors = data.errors;
  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (isRecord(item) && typeof item.message === 'string' && item.message.trim().length > 0) {
        return item.message;
      }
    }
  }

  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  return null;
}

function extractErrorResponseData(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) {
    return null;
  }

  const response = error.response;
  if (!isRecord(response)) {
    return null;
  }

  const data = response.data;
  return isRecord(data) ? data : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isGenericHttpStatusMessage(message: string): boolean {
  return /^Request failed with status code \d{3}$/u.test(message.trim());
}
